import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Procedure from '../../../../components/procedure/Procedure.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { getImagingUltrasoundSelector } from '../selectors'
import {
  setLocation,
  setFindings,
  setType,
  setUpdateInputRows,
  saveImagingUltrasound,
} from '../store/ImagingUltrasound.actions'
import { ImagingUltrasoundConstants } from '../store/imagingUltrasound.constants'
import { chevronBackOutline } from 'ionicons/icons'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import {
  addImagingUltrasoundItem,
  setDeleteProcedure,
  setProcedureLoaderItem,
} from '../../../timeline/store/timeline.actions'
import { getImagingUltrasoundOptionsSelector } from '../../../options/selectors'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { IOption } from '../../../../common/common.types'
import { initialImagingUltrasoundState } from '../store/ImagingUltrasound.reducers'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const ImagingUltrasoundProcedure: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const imagingUltrasoundProcedure = useSelector(getImagingUltrasoundSelector)
  const {
    time,
    date,
    pta,
    assessedBy,
    procedureComplications,
    authorization,
    imageType,
    response,
    responsePNNV,
    comments,
    updateInputRows,
    rolePerforming,
    rolePerformingPNNV,
    location,
    findings,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = imagingUltrasoundProcedure

  const imagingUltrasoundOptions = useSelector(getImagingUltrasoundOptionsSelector)
  const { imagingUltrasoundLocationOptions, typeOptions, findingsOptions } = imagingUltrasoundOptions
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const locationOnChange = (option: IOption) => {
    dispatch(setLocation(option))
  }

  const findingsOnChange = (option: IOption) => {
    dispatch(setFindings(option))
  }

  const typeOnChange = (option: IOption) => {
    dispatch(setType(option))
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImagingUltrasoundConstants.SET_TYPE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('imagingUltrasound.type'),
            dataTestID: 'Type',
            options: typeOptions,
            action: typeOnChange,
            listHeader: t('imagingUltrasound.type'),
            selectedOption: imageType,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImagingUltrasoundConstants.SET_LOCATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('imagingUltrasound.location'),
            dataTestID: 'Location',
            options: imagingUltrasoundLocationOptions,
            action: locationOnChange,
            listHeader: t('imagingUltrasound.location'),
            selectedOption: location,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: ImagingUltrasoundConstants.SET_FINDINGS,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('imagingUltrasound.findings'),
            dataTestID: 'Findings',
            options: findingsOptions,
            action: findingsOnChange,
            listHeader: t('imagingUltrasound.findings'),
            selectedOption: findings,
          },
        },
      ],
    },
  ]

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(imagingUltrasoundProcedure, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialImagingUltrasoundState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      dispatch(addImagingUltrasoundItem())
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'imagingUltrasound/SET_TIME', time: time })
              dispatch({ type: 'imagingUltrasound/SET_DATE', date: date })
              dispatch(saveImagingUltrasound())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: imagingUltrasoundProcedure.key,
                rowActionType: TimelineConstants.SET_IMAGING_ULTRASOUND_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        dispatch(addImagingUltrasoundItem())
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='ImagingUltrasoundPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Imaging / Ultrasound</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'imagingUltrasound'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          procedureComplications={procedureComplications}
          authorization={authorization}
          comments={comments}
          response={response}
          responsePNNV={responsePNNV}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default ImagingUltrasoundProcedure
